import {Component, Input, OnChanges, SimpleChanges, Output, EventEmitter, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {ApplicationAddressDTO} from '../../../../../models/application/ApplicationAddressDTO';
import {ApplicationDTO} from '../../../../../models/application/ApplicationDTO';
import {SharedService} from '../../../../../../common/services/shared-service';
import {finalize, take} from 'rxjs/operators';
import {OnboardingAdminPortalService} from '../../../../../services/onboard-application.service';
import {
  ApplicationAddressDialogComponent
} from '../../../../dialogs/application-address-update-dialog/application-address-update-dialog';
import {UserAuthorisationService} from '../../../../../../common/services/user-authorisation.service';
import {DisplayStrings} from '../../../../../../common/constants/DisplayStrings';
import {ApplicationUtilityService} from '../../../../../services/application-utility.service';

enum TableColumns {
  AddressType = 'addressType',
  AddressLine1 = 'addressLine1',
  AddressLine2 = 'addressLine2',
  CityTownCode = 'cityTownCode',
  Suburb = 'suburb',
  PostalCode = 'postalCode',
  Country = 'country',
  POBoxNumber = 'poBoxNumber',
  Actions = 'actions'
}

@Component({
  selector: 'greydom-application-addresses',
  templateUrl: './application-addresses.component.html',
  styleUrls: []
})
export class ApplicationAddressesComponent implements OnInit, OnChanges {
  readonly tableColumns = TableColumns;
  readonly DisplayStrings = DisplayStrings;

  @Input() application: ApplicationDTO;
  @Input() userFirstLastName = '';
  @Output() addressesUpdated = new EventEmitter<ApplicationAddressDTO[]>();

  addresses: ApplicationAddressDTO[];
  displayedColumns: string[] = Object.values(TableColumns);
  isProcessing = false;
  hasOnboardingQCRole: boolean;
  isApplicationLocked = false;


  constructor(
    private dialog: MatDialog,
    private sharedService: SharedService,
    private applicationService: OnboardingAdminPortalService,
    private userAuthorisationService: UserAuthorisationService,
    private applicationUtilityService: ApplicationUtilityService
  ) {}

  ngOnInit() {
    this.hasOnboardingQCRole = this.userAuthorisationService.hasOnBoardingQC() || this.userAuthorisationService.hasRoleEnterpriseManager();
    this.isApplicationLocked = this.applicationUtilityService.isApplicationLocked(this.application?.status?.applicationStatusId, this.application?.stage?.applicationStageId);
  }

  get isDataExist(): boolean {
    return !!(this.addresses && this.addresses.length);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.application && changes.application.currentValue) {
      this.addresses = this.application.membership?.addresses;
    }
  }

  getDisplayAddress1(addressDTO: ApplicationAddressDTO): string {
    return `${addressDTO.addressLine1Number ?? ''} ${addressDTO.addressLine1Name ?? ''}`;
  }

  getDisplayAddress2(addressDTO: ApplicationAddressDTO): string {
    return `${addressDTO.addressLine2Number ?? ''} ${addressDTO.addressLine2Name ?? ''}`;
  }

  onEditAddress(address: ApplicationAddressDTO): void {
    if (!this.hasOnboardingQCRole) {
      return;
    }

    const dialogRef = this.dialog.open(ApplicationAddressDialogComponent, {
      width: '40vw',
      data: {address}
    });

    dialogRef.afterClosed().subscribe((updatedAddress: ApplicationAddressDTO) => {
      if (updatedAddress) {
        const index = this.addresses.findIndex(a => a.addressTypeCode === address.addressTypeCode);
        if (index !== -1) {
          this.addresses[index] = updatedAddress;
          this.updateApplication();
        }
      }
    });
  }

  editTooltip(): string {
    const permissionMessage = DisplayStrings.permissionApplicationMessages?.rolePermissionUpdate;
    const statusMessage = DisplayStrings.permissionApplicationMessages?.statusUpdate;
    const editMessage = 'Edit Address';
    if (!this.hasOnboardingQCRole) {
      return permissionMessage;
    } else if (this.isApplicationLocked) {
      return statusMessage;
    } else {
      return editMessage;
    }
  }

  private updateApplication(): void {
    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;
    // Update Audit fields
    this.application.updated = new Date();
    this.application.updatedBy = this.userFirstLastName;
    this.applicationService.updateApplication(this.application)
      .pipe(
        take(1),
        finalize(() => this.isProcessing = false)
      )
      .subscribe({
        next: (response: ApplicationDTO) => {
          if (response) {
            this.application = response;
            this.addresses = response.membership?.addresses;
            this.addressesUpdated.emit(this.addresses);
            this.sharedService.openSnackBar('Address updated successfully', 'Dismiss');
          }
        },
        error: (error) => {
          console.error('Error updating application', error);
          this.sharedService.openErrorDialog(error.message);
        }
      });
  }

}
